
import React, {useEffect, useRef, useState} from "react";
import {Link, useParams} from "react-router-dom";
import Loading from "../../components/Loading/Loading";
import AllocationList from "../../components/AllocationList/AllocationList";
import fetchModel from "../../models/fetchModel";
import authModel from "../../models/authModel";
import ApiErrorMessage from "../../components/ApiErrorMessage/ApiErrorMessage";
import { handleEventListResponse } from "../../utils/handleFetchResponse";

import './_Events.scss';
import dateModel from "../../models/dateModel";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";

function useFirstRender() {
  const ref = useRef(true);
  const firstRender = ref.current;
  ref.current = false;
  return firstRender;
}

let Events = function({userRole, userType}) {
  let params = useParams()
  const organiserUserId = params.userId;
  const resolvedUserType = userType ?? authModel.getUserType();
  const organiserRouteAccessError = authModel.getOrganiserUserRouteAccessError(
    resolvedUserType,
    organiserUserId,
    userRole
  );
  const isOrganiserRouteAccessDenied = organiserRouteAccessError != null;
  let [events, setEvents] = useState([]);
  let [isLoading, setIsLoading] = useState(false);
  let [isUpcoming, setIsUpcoming] = useState(true);
  let [showAttendees, setShowAttendees] = useState(false);
  let firstRender = useFirstRender();
  let [attendees, setAttendees] = useState([]);
  let [loadError, setLoadError] = useState(null);

  useEffect(() => {
    getAllocations();
    // eslint-disable-next-line
  }, [isUpcoming, organiserUserId, resolvedUserType]);

  // Get /allocations
  const getAllocations = function() {
    if (organiserRouteAccessError) {
      setLoadError({ message: organiserRouteAccessError, retryable: false });
      setIsLoading(false);
      return;
    }

    const postBody = {};

    if (isLoading && !firstRender) {
      return;
    }

    setLoadError(null);
    setIsLoading(true);

    if (!isUpcoming) {
      postBody.isUpcoming = false;
    } else {
      postBody.isUpcoming = true;
    }

    if (authModel.shouldIncludeOrganiserUserId(resolvedUserType, organiserUserId)) {
      postBody.userId = Number(organiserUserId);
    }

    const responseCallback = (response) => {
      handleEventListResponse(
        response,
        (eventList, newAttendees) => {
          setEvents(eventList);
          setAttendees(newAttendees);
          setLoadError(null);
          setIsLoading(false);
        },
        (error) => {
          setEvents([]);
          setAttendees([]);
          setLoadError(error);
          setIsLoading(false);
        }
      );
    };

    if (userRole === 'staff') {
      const url = "/staff/event-list";
      fetchModel.postStaff(url, postBody, responseCallback);
    } else {
      const url = "/event-list";
      fetchModel.post(url, postBody, responseCallback);
    }
  }

  function updateShowAttendees() {
    setShowAttendees(true)
  }

  function updateDontShowAttendees() {
    setShowAttendees(false)
  }

  function eventsTemplate() {
    if (isOrganiserRouteAccessDenied) {
      return null;
    }

    if (isLoading || firstRender) {
      return <div className="Allocations-loading">
        <Loading />
      </div>
    }

    if (loadError) {
      return null;
    }

    if (events.length === 0) {
      return <p>No events found</p>;
    }

    if (events.length > 0) {
      return (
        <>
          <ButtonGroup buttons={[
            {
              'text': 'Events',
              'isActive': !showAttendees,
              'callback': updateDontShowAttendees
            },
            {
              'text': 'Attendees',
              'isActive': showAttendees,
              'callback': updateShowAttendees
            },

          ]}></ButtonGroup>

          {!showAttendees ? (
            <>
              {events.map((thisEvent, index) => (
                <div
                  key={index}
                  className="Allocations-event-row"
                >
                  <div className="Allocations-event">
                    <h2 className="Allocations-event-name">
                      <Link
                        to={`/allocations/${thisEvent.id}`}
                        className="Allocations-event-name-link">
                        {thisEvent.name}
                      </Link>
                    </h2>
                    <p>{thisEvent.subtitle}</p>
                    <p>{dateModel.formatFromDb(thisEvent.date)}</p>
                    <p>{thisEvent.currency_symbol}{thisEvent.price}</p>
                    <p>{thisEvent.sold} sold</p>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <AllocationList allocations={attendees} userRole={userRole}></AllocationList>
          )}
        </>
      )
    }
  }

  function updateIsUpcoming() {
    setIsUpcoming(true)
  }

  function updateIsNotUpcoming() {
    setIsUpcoming(false)
  }

  return (
    <main className="Allocations">
      <h1>Event list{organiserUserId ? ` (organiser ${organiserUserId})` : ''}</h1>
      <div className="Allocations-eventList">
        <ApiErrorMessage error={loadError} onRetry={getAllocations} />
        {isOrganiserRouteAccessDenied ? (
          <Link to="/allocations" className="Button">Back to all events</Link>
        ) : !params.id ? (
            <ButtonGroup buttons={[
              {
                'text': 'Today/Upcoming',
                'callback': updateIsUpcoming,
                isActive: isUpcoming
              },
              {
                'text': 'Past',
                'callback': updateIsNotUpcoming,
                isActive: !isUpcoming
              },

            ]}></ButtonGroup>
          ) : (
            <Link to="/allocations" className="Button">Back to all events</Link>
          )
        }
        {eventsTemplate()}
      </div>
    </main>
  );
}

export default Events;
